import ButtonLink from '@components/common/ButtonLink';
import actionTypes from '@context/actionTypes';
import { store } from '@context/store';
import Product from '@customTypes/product';
import { GuestGroupType } from '@customTypes/product-details';
import { areChildrenAllowed } from '@helpers/product';
import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { CartItemDetails } from '@customTypes/cart-items';
import useCounterValidation from '@hooks/useCounterValidation';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import FormErrorText from './FormErrorText';
import CounterVariant from './CounterVariant';

type Props = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  group: GuestGroupType;
  index: number;
  product: Product;
  setTempBag: Dispatch<SetStateAction<GuestGroupType[]>>;
  tempBag: GuestGroupType[];
  hideHeader: boolean;
};

function GuestGroupVariant({
  cartData,
  date,
  group,
  index,
  product,
  setTempBag,
  tempBag,
  hideHeader,
}: Props) {
  const [adultCounter, setAdultCounter] = useState<number>(product.max_adults_occupancy);
  const [childrenCounter, setChildrenCounter] = useState<number>(0);

  const { dispatch } = useContext(store);

  const { ctasUpdateExperiment } = useFeatureFlagContext();
  const { counterValidation, errorMessage, showErrorMessage } = useCounterValidation(
    adultCounter,
    childrenCounter,
    cartData,
    product,
  );

  const productName = useMemo(() => {
    let { name } = product;
    if (tempBag && tempBag.length > 1) {
      name += ` #${index + 1}`;
    }

    return name;
  }, [tempBag, index, product]);

  const handleOnRemoveClick = () => {
    const newBag = tempBag.filter((guestGroup: GuestGroupType) => group.id !== guestGroup.id);

    setTempBag(newBag);

    // If bag is empty close the drawer
    if (!newBag.length) {
      dispatch({
        type: actionTypes.SET_GUEST_GROUPS_BAG,
        payload: [],
      });
    }
  };

  useEffect(() => {
    const updatedBagItem = tempBag.map((item) => {
      if (item.id === group.id) {
        return { ...item, adults: adultCounter, children: childrenCounter };
      }
      return item;
    });

    setTempBag(updatedBagItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adultCounter, childrenCounter]);

  useEffect(() => {
    dispatch({
      type: actionTypes.SET_GUEST_GROUPS_BAG,
      payload: tempBag,
    });
  }, [dispatch, setTempBag, tempBag]);

  return (
    <div
      key={group.id}
      className={`flex flex-col justify-center ${!hideHeader ? 'px-4' : ''} py-2 w-full`}
    >
      {!hideHeader ? (
        <div className="flex items-center justify-between">
          <p className="font-rp-pn-semi-bold leading-6 text-18">{productName}</p>

          <ButtonLink onClick={handleOnRemoveClick} text="Remove" />
        </div>
      ) : null}
      <div className={`flex flex-row items-center justify-between ${!hideHeader ? 'mt-6' : ''}`}>
        <div className="flex flex-col">
          <div className="flex items-baseline">
            <p className="font-rp-pn-semi-bold leading-6 text-18">Adults</p>

            {product.adult_age_restriction_text && (
              <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-xs">
                {product.adult_age_restriction_text}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <CounterVariant
            dispatch={dispatch}
            setParentCounter={setAdultCounter}
            counter={adultCounter}
            productData={product}
            date={date}
            personType="adults"
            validate={counterValidation}
            ctasUpdateExperiment={ctasUpdateExperiment}
          />
        </div>
      </div>

      {areChildrenAllowed(product) && (
        <div
          className={`flex flex-col pt-6 ${
            !hideHeader ? 'border-t mt-7' : ''
          } border-rp-gray-divider`}
        >
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <div className="flex items-baseline">
                <p className="font-rp-pn-semi-bold leading-6 text-18">Children</p>

                {product.adult_age_restriction_text && (
                  <p className="first-letter-uppercase font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                    {product.child_age_restriction_text}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <CounterVariant
                dispatch={dispatch}
                setParentCounter={setChildrenCounter}
                counter={childrenCounter}
                productData={product}
                date={date}
                personType="children"
                validate={counterValidation}
                ctasUpdateExperiment={ctasUpdateExperiment}
              />
            </div>
          </div>
        </div>
      )}

      {product.infants_allowed && (
        <div
          className={`${
            !hideHeader ? 'border-t mt-7' : ''
          } border-rp-gray-divider flex flex-row items-center justify-between pt-6`}
        >
          <div className="flex items-baseline">
            <p className="font-rp-pn-semi-bold leading-6 text-18">Infant</p>

            {product.min_child_age && (
              <p className="capitalize font-rp-pn-regular leading-18px ml-2 opacity-50 text-sm">
                Over {product.min_child_age}
              </p>
            )}
          </div>

          <p className="leading-6 mr-1 text-md">Free</p>
        </div>
      )}

      {/* Error message */}
      {showErrorMessage && (
        <div className="mb-6">
          <FormErrorText showInfoIcon text={errorMessage} />
        </div>
      )}
    </div>
  );
}

export default GuestGroupVariant;
